import {getName} from '@esgi/ui-kit/utils';
import {ComponentType, ErrorInfo, PureComponent} from 'react';

type ErrorHandler = (error: Error) => void;
export type DefaultErrorHandlerRef = { handle: ErrorHandler };

const errorHandler: DefaultErrorHandlerRef = {
	handle: (e) => {
		console.error('[UI-Kit Error Reporter]:', e);
	},
};

export function registerDefaultErrorHandler(callback: ErrorHandler): void {
	errorHandler.handle = (error) => {
		callback(error);
	};
}

class ErrorBoundaryState {
	hasError: boolean = false;
}

export type ErrorHandlingProps = { onCatchError?: ErrorHandler };

export function withErrorHandling<T>(Component: ComponentType<T>) {
	class ErrorHandling extends PureComponent<T & ErrorHandlingProps, ErrorBoundaryState> {
		public override readonly state = new ErrorBoundaryState();

		public override render() {
			const {onCatchError, ...props} = this.props;
			if (this.state.hasError) {
				return null;
			}

			return <Component {...props as T}/>;
		}

		public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
			this.setState({hasError: true}, () => this.handleError(error));
		}

		private handleError = (error: Error) => {
			try {
				if (this.props.onCatchError) {
					this.props.onCatchError(error);
				}

				defaultErrorHandler.handle(error);
			} catch (e) {
				console.error(e);
			}
		};
	}

	(ErrorHandling as any).displayName = getName(Component);
	return ErrorHandling;
}

export const defaultErrorHandler: Readonly<DefaultErrorHandlerRef> = errorHandler;
